import { XIcon } from "lucide-react";
import React, { ChangeEvent, useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUserId, setToken, setRefreshToken, setFullName, setGoogleLocationApiKey } from "../../store/slices/userSlice";
import { toast } from "sonner";
import { authApi } from "../../lib/api";
import { Loader } from "../../components/ui/loader";
import SuccessPopup from "../../components/ui/success-popup";
import { useTranslation } from "react-i18next";
import bgImage from "../../assets/MypredictionsDashboard.png"

interface LocationState {
  mobileNumber: string;
  countryCode: string;
}

export const Register = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [name, setName] = React.useState("");
  const [isRegistering, setIsRegistering] = React.useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const state = location.state as LocationState;
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";

  // Check if we have the required state
  useEffect(() => {
    if (!location.state?.mobileNumber) {
      toast.error(t('invalidAccess'));
      if (country) {
        navigate(`/${country}/login`);
      } else {
        navigate('/in/login');
      }
    }
  }, [location.state, navigate, t, country]);

  // Effect to handle success popup timing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (showSuccessPopup) {
      timeoutId = setTimeout(() => {
        setShowSuccessPopup(false);
      }, 2000); // Show popup for 2 seconds
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showSuccessPopup]);

  // If no mobile number in state, return a loading state
  if (!location.state?.mobileNumber) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader isLoading={true} message={t('redirecting')} />
      </div>
    );
  }

  // Function to check if name is valid (not empty and not just spaces)
  const isNameValid = (value: string) => {
    return value.trim().length > 0;
  };

  const handleGoBack = () => {
    if (country) {
      navigate(`/${country}/otp`);
    } else {
      navigate('/in/otp');
    }
  };

  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      const cleanCountryCode = state.countryCode.replace("+", "");
      const trimmedName = name.trim(); // Trim spaces before sending to API
      const response = await authApi.register(trimmedName, state.mobileNumber, cleanCountryCode);
      toast.success(response.message);
      dispatch(setUserId(response.data.id.toString()));
      dispatch(setToken(response.accessToken));
      dispatch(setRefreshToken(response.refreshToken));
      dispatch(setFullName(response.data.fullName));

      // Fetch and store config
      try {
        const configResponse = await authApi.getConfig();
        if (configResponse.data?.googleLocationConfig?.apiKey) {
          dispatch(setGoogleLocationApiKey(configResponse.data.googleLocationConfig.apiKey));
        }
      } catch (configError) {
        console.error('Error fetching config:', configError);
        // Don't block the flow if config fetch fails
      }

      setShowSuccessPopup(true); // Show success popup
      // Navigate after popup is shown
      setTimeout(() => {
        if (country) {
          navigate(`/${country}/my-predictions`);
        } else {
          navigate('/in/my-predictions');
        }
      }, 2000);
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(t('errorCreatingUser'));
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <>
      <Loader isLoading={isRegistering} message={t('creatingAccount')} />
      <SuccessPopup handlepopup={showSuccessPopup} message={t('registrationSuccessful')} />
      <div className="bg-no-repeat flex flex-col items-center justify-center min-h-screen bg-cover" style={{ backgroundImage: `url(${bgImage})` }}>
        <div className="flex items-center justify-center w-full h-dvh bg-[#444444BD]" style={{backdropFilter:'blur(5px)'}}>
        <div className="w-full max-w-md p-4">
          <Card className="w-full bg-white">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">{t('enterYourName')}</h1>
                <button onClick={handleGoBack}>
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <Input
                type="text"
                placeholder={t('enterNamePlaceholder')}
                value={name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (value.length <= 50) {
                    setName(value);
                  }
                }}
                maxLength={50}
                className="mb-4"
              />

              <Button
                className="w-full bg-green-700 hover:bg-green-800 text-white py-6 rounded-md mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleRegister}
                disabled={!isNameValid(name)}
              >
                {t('continue')}
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </>
  );
};